/**
 * NumberInput component module.
 * @module shared/components/NumberInput
 *
 */
import React, {useEffect, useRef} from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components/macro';
import NumberFormat from 'react-number-format';
import {fill, layoutAttribute} from './cssUtils';
import TitleLabel from './TitleLabel';
import {getHexWithOpacity} from '../utils/colors';

const _Wrapper = styled.span`
  display: inline-block;
  opacity: ${props => props.disabled ? 0.5 : 1};
  height: ${props => props.height};
  ${props => fill(props.width)}
  ${props => props.margin ? `margin: ${props.margin}` : 'margin: 5px'};
  ${layoutAttribute.css};
`;

const _Input = styled(NumberFormat)`
  display: block;
  width: 100%;
  width: -moz-available;
  //noinspection CssInvalidPropertyValue
  width: -webkit-fill-available;
  //noinspection CssInvalidPropertyValue
  width: fill-available;
  //noinspection CssInvalidPropertyValue
  width: stretch;
  box-sizing: border-box;

  background-color: ${props => {
    if (props.theme.neumorphic) {
      return getHexWithOpacity(props.theme.tileBackgroundColor, props.readOnly ? 0 : 1);
    }
    if (props.readOnly) {
      return 'transparent';
    }

    return props.theme.dark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)';
  }};

  ${props => props.theme.neumorphic
    ? `background-image: linear-gradient(to bottom right, rgba(0, 0, 0, ${props.readOnly ? 0 : 0.2}), rgba(0, 0, 0, ${props.readOnly ? 0 : 0.2}))` : ''};
  ${props => props.theme.neumorphic
    ? `box-shadow: -1px -1px 0 0 rgba(0, 0, 0, ${props.readOnly ? 0 : 0.4}),
                   1px 1px 0 0 rgba(255, 255, 255, ${props.readOnly ? 0 : 0.1})` : ''};

  border-width: ${props => props.readOnly ? '3px 3px 3px 0' : '3px'};
  border-style: solid;
  border-color: rgba(255, 65, 65, 0);
  border-radius: 4px;
  padding: ${props => props.readOnly ? '7px 7px 7px 0' : '7px'};
  font-family: ${props => props.theme.bodyText.name};
  font-size: ${props => props.theme.bodyText.size};
  color: ${props => props.theme.dark ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.9)'};
  text-align: left;

  &::placeholder {
    font-family: ${props => props.theme.bodyText.name};
    font-size: ${props => props.theme.bodyText.size};
    color: ${props => props.theme.dark ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)'};
    text-align: left;
    width: 100%;
    width: -moz-available;
    //noinspection CssInvalidPropertyValue
    width: -webkit-fill-available;
    //noinspection CssInvalidPropertyValue
    width: fill-available;
    //noinspection CssInvalidPropertyValue
    width: stretch;
  }

  &:not(:placeholder-shown):invalid {
    border-color: rgba(255, 65, 65, 1);
  }

  &:focus {
    outline: none;
  }
`;

/**
 * NumberInput component
 * @name NumberInput
 */
const NumberInput = (props) => {
  const {
    title,
    type,
    placeholder,
    dataPoint,
    decimalPlaces = 0,
    width,
    height,
    help,
    readOnly,
    disabled,
    required,
    margin,
    validation,
    onBlur,
    layout,
  } = props;
  let ph = placeholder;

  if (ph === undefined) {
    let trailingZeroes = '';

    for (let i = 0; i < decimalPlaces; i++) {
      trailingZeroes += '0';
    }

    const placeholderDefaults = {
      number: (decimalPlaces <= 0) ? '0' : '0.' + trailingZeroes,
      currency: (decimalPlaces <= 0) ? '$0' : '$0.' + trailingZeroes,
      percent: (decimalPlaces <= 0) ? '0%' : '0.' + trailingZeroes + '%',
    };

    ph = placeholderDefaults[type];
  }

  function validate(element, val) {
    const {min, max, custom} = validation || {};

    if (((min !== undefined) && (val < min)) || ((max !== undefined) && (val > max))) {
      element.setCustomValidity('Value not within valid number range');
      return false; // element.reportValidity();
    }

    element.setCustomValidity('');

    if (custom) {
      const invalid = custom({name: element.name, type: type, value: val, min: min, max: max});
      element.setCustomValidity(invalid);
      return !invalid;
    }
    return true; // element.reportValidity();
  }

  const inputElement = useRef(null);

  useEffect(() => {
    if (props.validation && dataPoint.value != null) {
      if (inputElement.current) {
        validate(inputElement.current, dataPoint.value);
      }
    }
  }, []);

  const numberFormatProps = {
    allowLeadingZeros: false,
    allowNegative: true,
    decimalScale: (decimalPlaces < 0) ? 0 : decimalPlaces,
    fixedDecimalScale: true,
    thousandSeparator: true,
    displayType: 'input',
  };

  if (type === 'currency') {
    numberFormatProps.prefix = '$';
  } else if (type === 'percent') {
    numberFormatProps.suffix = '%';
  }

  const onBlurHandler = (target, parentHandler) => {
    if (!readOnly && target.value != dataPoint.value) { // eslint-disable-line eqeqeq
      const num = (target.value === '') ? null : Number(target.value.replace(/[$%,]/g, ''));
      const value = ((type === 'percent') && num) ? (num / 100) : num;

      parentHandler(dataPoint.name, value, !validate(target, value));
    }
  };

  return <_Wrapper width={width} height={height} disabled={disabled} layout={layout} margin={margin}>
    {title && <TitleLabel title={title} help={help}/>}
    <_Input
      getInputRef={inputElement}
      {...numberFormatProps}
      placeholder={readOnly ? '-' : ph}
      onBlur={e => onBlurHandler(e.target, onBlur)}
      onKeyDown={e => e?.key === 'Enter' && onBlurHandler(e.target, onBlur)}
      disabled={disabled}
      readOnly={readOnly}
      required={required}
      name={dataPoint.name}
      value={((type === 'percent') && dataPoint.value) ? (dataPoint.value * 100) : dataPoint.value}
      autoCorrect='off'
    />
  </_Wrapper>;
};

NumberInput.propTypes = {
  /**
   * the title of the input, to use for its displayed label and to be followed by a colon (optional)
   */
  title: PropTypes.string,
  /**
   * the type of the input: number, currency, or percent
   */
  type: PropTypes.oneOf(['number', 'currency', 'percent']).isRequired,
  /**
   * placeholder text; if given, must be specified exactly - component will not do any formatting of placeholder.
   * However, if undefined, will default to "0" value, with appropriate decimal places and formatting.
   */
  placeholder: PropTypes.string,
  /**
   * DataPoint component
   */
  dataPoint: PropTypes.shape({
    name: PropTypes.string,
    value: PropTypes.number,
  }).isRequired,
  /**
   * number of decimal places (optional)
   */
  decimalPlaces: PropTypes.number,
  /**
   * width of the input field, as CSS (e.g. '500px', '50%') (optional)
   */
  width: PropTypes.string,
  /**
   * height of the input field, as CSS (e.g. '500px', '50%') (optional)
   */
  height: PropTypes.string,
  /**
   * whether the input is read only (optional)
   */
  /**
   * If passed in, then help icon displays per content, title, and type properties
   */
  help: PropTypes.shape({
    /**
     * Help icon description
     */
    description: PropTypes.string,
    /**
     * Help type
     */
    type: PropTypes.oneOf(['action', 'modal', 'tooltip']).isRequired,
    /**
     * If help type is modal, the modal title
     */
    title: PropTypes.string,
    /**
     * If help type is modal, the modal body
     */
    content: PropTypes.node,
    /**
     * If help type is action, the action to perform
     */
    action: PropTypes.func,
    /**
     * If button needs to be customized, then pass the text, action, and data for button
     */
    button: PropTypes.shape({
      text: PropTypes.string,
      action: PropTypes.func,
      data: PropTypes.object,
    }),
  }),
  /**
   * Whether the input is read-only
   */
  readOnly: PropTypes.bool,
  /**
   * whether the input is disabled (optional)
   */
  disabled: PropTypes.bool,
  /**
   * whether the field is a required input (optional)
   */
  required: PropTypes.bool,
  /**
   * validation: minimum value, maximum value, or custom validation (optional)
   * for percent type, min and max values should be entered as decimal (e.g., 0.02 for 2%)
   */
  validation: PropTypes.shape({
    min: PropTypes.number,
    max: PropTypes.number,
    /**
     * Custom validation function needs to return empty string('') for valid and non-empty string for invalid;
     */
    custom: PropTypes.func,
  }),
  /**
   * function to link to state in parent (optional)
   *  - takes 3 params (name, value, invalid (optional))
   */
  onBlur: PropTypes.func,
  layout: layoutAttribute.propTypes,
  margin: PropTypes.string,
};

export default NumberInput;
